import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, deleteDoc, collection, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../AuthProvider';
import { useUI } from '../UIContext';
import { setDirtyFlag } from '../safeNavigate';

import PageWrapperAuto from '../components/PageWrapperAuto';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ConfirmLeaveModal from '../components/ConfirmLeaveModal'; // reuse your modal
import { normalizeKey, normalizeType, keyRoot, normalizeGenre, getNormalizedGenreOptions } from '../utils/normalize';
import CustomTagInput from '../components/CustomTagInput';
import { useUserPrefs } from '../UserPrefsProvider';
import { loadSetHybrid } from '../utils/loadSetHybrid';
import dexie from '../offlineDB';
import { addPendingWrite, flushPendingWrites } from '../utils/flushPendingWrites'; // ⬅️ Make sure this is at the top
import { normalizeTagsArray, normalizeTag } from '../utils/normalize';


export default function EditSetPage() {

  const { inputStyle, textStyle } = useUI();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: setId } = useParams();
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [rating, setRating] = useState('');
  const [labelOptions, setLabelOptions] = useState([]);

  const [tunes, setTunes] = useState([]);
  const [wasCreatedEmpty, setWasCreatedEmpty] = useState(false);

  const [isDirty, setIsDirty] = useState(false);

  const [allTunes, setAllTunes] = useState([]);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterKey, setFilterKey] = useState('');
  const [filterGenre, setFilterGenre] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showLeaveModal, setShowLeaveModal] = useState(false);
const [pendingLeaveAction, setPendingLeaveAction] = useState(null);
const [customTags, setCustomTags] = useState([]);
const { prefs } = useUserPrefs();
const [allSets, setAllSets] = useState([]);
const [itemTypeFilter, setItemTypeFilter] = useState('all'); // 'all' | 'tune' | 'set'
const [isSaving, setIsSaving] = useState(false); // 🔁 debounce
const [saveNotice, setSaveNotice] = useState(null);


 useEffect(() => {
  if (!user) return;

  const unsubTunes = onSnapshot(
    collection(db, 'users', user.uid, 'tunes'),
    snapshot => {
      setAllTunes(snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        itemType: 'tune',
      })));
    }
  );

  const unsubSets = onSnapshot(
    collection(db, 'users', user.uid, 'sets'),
    snapshot => {
      setAllSets(
        snapshot.docs
          .filter(doc => doc.id !== setId) // avoid self-inclusion
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            itemType: 'set',
          }))
      );
    }
  );

  if (setId) {
    const load = async () => {
      try {
        if (navigator.onLine) {
          await flushPendingWrites(user); // ✅ sync offline edits first
        }

        const data = await loadSetHybrid(user, setId);
        if (data) {
          setTitle(data.title || '');
          setNotes(data.notes || '');
          setTunes(data.tunes || []);
          setRating(data.rating || '');
          setCustomTags(data.customTags || []);

          if (!data.title && (!data.tunes || data.tunes.length === 0)) {
            setWasCreatedEmpty(true);
          }
        }
      } catch (err) {
        console.error("Error loading set:", err);
      }
    };

    load();
  }

  return () => {
    unsubTunes();
    unsubSets();
  };
}, [user, setId]);






    const handleAdd = (tune) => {
      if (tunes.find(t => t.id === tune.id)) return;
      setTunes(prev => [...prev, tune]);

      // ✅ Set title automatically if it's still blank
      if (!title) setTitle(tune.title);
      setIsDirty(true);
    };



    const handleRemove = (id) => {
      setTunes(prev => prev.filter(t => t.id !== id));
      setIsDirty(true);
    };


const handleSave = async () => {
  if (!user) return;
  if (isSaving) return;
  setIsSaving(true);
  setSaveNotice(null);

  const updatedData = {
    title: title.trim() || 'Untitled Set',
    notes: notes.trim(),
    tunes,
    rating,
    customTags: normalizeTagsArray(customTags),
    updatedAt: new Date().toISOString()
  };

  const ref = doc(db, 'users', user.uid, 'sets', setId);

  // 🔁 Sync new tags to user preferences
  try {
    const prefsRef = doc(db, 'users', user.uid, 'preferences', 'settings');
    const prefsSnap = await getDoc(prefsRef);
    const prefsData = prefsSnap.exists() ? prefsSnap.data() : {};
    const existingPrefsTags = Array.isArray(prefsData.customTags) ? prefsData.customTags : [];

    const mergedTags = Array.from(new Set([
      ...normalizeTagsArray(customTags || []),
      ...existingPrefsTags.map(normalizeTag)
    ]));

    if (navigator.onLine) {
      await updateDoc(prefsRef, { customTags: mergedTags });
    } else {
      await addPendingWrite({
        id: 'settings',
        type: 'update',
        collection: 'preferences',
        docId: 'settings',
        data: { customTags: mergedTags }
      });
    }
  } catch (err) {
    console.warn('Could not sync tags to user preferences:', err);
  }

  // 💾 Save the set
  try {
    if (navigator.onLine) {
      await updateDoc(ref, updatedData);
      await dexie.sets.put({ id: setId, ...updatedData });
    } else {
      await dexie.sets.put({ id: setId, ...updatedData });
      await addPendingWrite({
        id: setId,
        type: 'update',
        collection: 'sets',
        docId: setId,
        data: { id: setId, ...updatedData }
      });
    }

    setIsDirty(false);
    setSaveNotice({ tone: 'success', text: 'Set saved.' });
    navigate(`/sets/${setId}`);
  } catch (err) {
    console.error('Error saving set:', err);
    setSaveNotice({ tone: 'error', text: 'Failed to save set.' });
  } finally {
    setIsSaving(false);
  }
};



    const handleDragEnd = (result) => {
      if (!result.destination) return;
      const reordered = Array.from(tunes);
      const [moved] = reordered.splice(result.source.index, 1);
      reordered.splice(result.destination.index, 0, moved);
      setTunes(reordered);
      setIsDirty(true);
    };


  
  const keyGroups = {};
  allTunes.forEach(t => {
    const nk = normalizeKey(t.key);
    if (!nk) return;
    const root = keyRoot(nk);
    if (!keyGroups[root]) keyGroups[root] = new Set();
    keyGroups[root].add(nk);
  });
const genreOptions = getNormalizedGenreOptions(allTunes.map((t) => t.genre));

const combinedItems = [...allTunes, ...allSets];

const filtered = combinedItems.filter(item => {
  if (itemTypeFilter !== 'all' && item.itemType !== itemTypeFilter) return false;

  const query = search.toLowerCase();
  const nk = normalizeKey(item.key || '');
  const matchSearch = !searchQuery || Object.values(item).some(val =>
    typeof val === 'string' && val.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const matchTitle = item.title?.toLowerCase().includes(query);
  const matchType = filterType ? normalizeType(item.type) === normalizeType(filterType) : true;
  const matchKey = !filterKey
    || nk === filterKey
    || (filterKey.startsWith('root:') && keyRoot(nk) === filterKey.replace('root:', ''));
  const matchGenre = !filterGenre || normalizeGenre(item.genre) === filterGenre;

  return matchSearch && matchTitle && (item.itemType === 'set' || (matchType && matchKey && matchGenre));
});



  const formatTuneLabel = (t) => {
    const parts = [];
    if (t.type) parts.push(t.type);
    if (t.key) parts.push(t.key);
    return parts.length > 0 ? ` (${parts.join(', ')})` : '';
  };

useEffect(() => {
  const handler = (e) => {
    if (isDirty) {
      e.preventDefault();
      e.returnValue = '';
    }
  };
  window.addEventListener('beforeunload', handler);
  return () => window.removeEventListener('beforeunload', handler);
}, [isDirty]);

useEffect(() => {
  setDirtyFlag(isDirty); // ✅ sync global flag any time isDirty changes
}, [isDirty]);

useEffect(() => {
  return () => {
    setIsDirty(false);    // ✅ reset local flag on unmount
    setDirtyFlag(false);  // ✅ reset global flag on unmount
  };
}, []);


  return (
    <PageWrapperAuto>
      <h2>Edit Set</h2>
      {saveNotice && (
        <div
          style={{
            margin: '0.35rem auto 0.8rem',
            maxWidth: '420px',
            padding: '8px 10px',
            borderRadius: '8px',
            textAlign: 'center',
            backgroundColor: saveNotice.tone === 'error' ? '#5a1f1f' : '#1f3f5a',
            color: '#fff',
            fontSize: '0.9rem'
          }}
        >
          {saveNotice.text}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '400px', margin: '0 auto 20px' }}>
        <input
          style={inputStyle}
          value={title}
          onChange={e => {
            setTitle(e.target.value);
            setIsDirty(true);
          }}
          placeholder="Set title"
        />

      <textarea
        style={inputStyle}
        value={notes}
        onChange={e => {
          setNotes(e.target.value);
          setIsDirty(true);
        }}
        placeholder="Notes (optional)"
        rows={3}
      />

      <select
        style={inputStyle}
        value={rating}
        onChange={e => {
          setRating(e.target.value);
          setIsDirty(true);
        }}
      >
        <option value="">Rating</option>
        <option value="★">★</option>
        <option value="★★">★★</option>
        <option value="★★★">★★★</option>
        <option value="★★★★">★★★★</option>
        <option value="★★★★★">★★★★★</option>
      </select>

<div style={{ marginTop: '0rem' }}>
  <h4 style={{ marginBottom: '0.25rem' }}>Tags</h4>
  <CustomTagInput
    tags={customTags}
    setTags={(tags) => {
      setCustomTags(tags);
      setIsDirty(true);
    }}
    suggestions={prefs?.customTags || []}
    inputStyle={inputStyle}
  />
</div>




      </div>

      <h3>Tunes in Set</h3>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="tuneList">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{
                maxHeight: '200px',
                overflowY: 'auto',
                border: 'none',
                borderRadius: '8px',
                padding: '6px',
                backgroundColor: 'var(--input-bg)',
                color: 'inherit',
                minHeight: '40px'
              }}
            >
              {tunes.map((t, index) => (
                <Draggable key={`${t.id || t.title}-${index}`} draggableId={`${t.id || t.title}-${index}`} index={index}>
                  {(provided) => {
                    const style = {
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: 'var(--item-bg, #f9f9f9)',
                      color: 'var(--text-color, #000)',
                      padding: '8px',
                      borderBottom: '1px solid #ccc',
                      ...provided.draggableProps.style
                    };
                    return (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={style}
                      >
                        <span><span className="text-scaled">{t.title}{formatTuneLabel(t)}</span></span>
                        <button onClick={() => handleRemove(t.id)} style={{ ...inputStyle, marginLeft: '10px' }}>×</button>
                      </div>
                    );
                  }}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <h3>Add Tunes</h3>
<div style={{ display: 'flex', justifyContent: 'center' }}>
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '100%', gap: '10px' }}>

    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
      <input
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search by title"
          style={inputStyle}
      />
      <input
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        placeholder="Search any field"
          style={inputStyle}
      />
    </div>

    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px', justifyContent: 'center' }}>
      <select
        value={filterGenre}
        onChange={e => setFilterGenre(e.target.value)}
          style={inputStyle}
      >
        <option value="">All genres</option>
        {genreOptions.map((g, i) => (
          <option key={i} value={g}>{g}</option>
        ))}
      </select>

      <select
        value={filterType}
        onChange={e => setFilterType(e.target.value)}
          style={inputStyle}
      >
        <option value="">All types</option>
        {[...new Set(allTunes.map(t => normalizeType(t.type)).filter(Boolean))].sort().map(type => (
          <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
        ))}
      </select>

      <select
        value={filterKey}
        onChange={e => setFilterKey(e.target.value)}
          style={inputStyle}
      >
        <option value="">All Keys</option>
        {Object.keys(keyGroups).sort().map(root => (
          <optgroup key={root} label={root}>
            <option value={`root:${root}`}>{root} (All Modes)</option>
            {[...keyGroups[root]].sort((a, b) => {
              const rank = k => {
                if (k.endsWith(' mix')) return 3;
                if (k.endsWith(' dorian')) return 2;
                if (k.endsWith('m')) return 1;
                return 0;
              };
              return rank(a) - rank(b) || a.localeCompare(b);
            }).map(k => {
              const label =
                k.endsWith('m') ? root + ' minor' :
                k.endsWith(' dorian') ? root + ' dorian' :
                k.endsWith(' mix') ? root + ' mixolydian' :
                root + ' major';
              return <option key={k} value={k}>{label}</option>;
            })}
          </optgroup>
        ))}
      </select>

      <select
        value={itemTypeFilter}
        onChange={(e) => setItemTypeFilter(e.target.value)}
        style={inputStyle}
      >
        <option value="all">Tunes & Sets</option>
        <option value="tune">Tunes only</option>
        <option value="set">Sets only</option>
      </select>

    </div>
  </div>
</div>


      <div style={{ listStyleType: 'none', paddingLeft: 0, maxHeight: '200px', overflowY: 'auto', border: 'none', borderRadius: '8px', padding: '6px', backgroundColor: 'var(--input-bg)' }}>
{filtered.map(item => {
  const inSet = tunes.find(existing => existing.id === item.id && existing.type === item.itemType);
  const isSet = item.itemType === 'set';
  return (
    <div
      key={item.id}
      className="text-scaled"
      style={{
        cursor: inSet ? 'default' : 'pointer',
        color: inSet ? 'var(--text-muted, #999)' : 'inherit',
        backgroundColor: inSet ? 'var(--button-bg, #f0f0f0)' : 'transparent',
        fontWeight: inSet ? 'bold' : 'normal',
        padding: '4px 0'
      }}
      onClick={() => {
        if (inSet) return;
        const newEntry = {
          id: item.id,
          title: item.title || '',
          type: item.itemType,
          key: item.key || '',
          tuneType: item.type || '',
          genre: item.genre || '',
        };
        if (!title && item.title) setTitle(item.title);
        setTunes(prev => [...prev, newEntry]);
        setIsDirty(true);
      }}
    >
      <span className="text-scaled">
        {item.title}
        {isSet ? ' (Set)' : formatTuneLabel(item)}
      </span>
    </div>
  );
})}

      </div>

      <div className="save-cancel-row">
        <button className="btn-subtle btn-commit" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Set'}
        </button>
<button
  className="btn-subtle"
  onClick={async () => {
    const cleanup = async () => {
      if (wasCreatedEmpty && user && setId) {
        const ref = doc(db, 'users', user.uid, 'sets', setId);
        try {
          if (navigator.onLine) {
            await deleteDoc(ref);
            await dexie.sets.delete(setId);
          } else {
            await dexie.sets.delete(setId);
            await addPendingWrite({
              id: setId,
              type: 'delete',
              collection: 'sets',
              docId: setId
            });
          }
        } catch (err) {
          console.error('Failed to delete empty set:', err);
        }
      }

      navigate('/sets');
    };

    if (isDirty) {
      setPendingLeaveAction(() => cleanup);
      setShowLeaveModal(true);
    } else {
      await cleanup();
    }
  }}
>
  Cancel
</button>

      </div>

      <ConfirmLeaveModal
  open={showLeaveModal}
  onCancel={() => setShowLeaveModal(false)}
  onConfirm={() => {
    setShowLeaveModal(false);
    setIsDirty(false);
    if (pendingLeaveAction) pendingLeaveAction();
  }}
/>

    </PageWrapperAuto>
  );
}
